import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ArrowRight, BarChart3, Image as ImageIcon, MessageSquare, QrCode, RefreshCw, Smartphone, SquareStack, } from 'lucide-react';
const PAGE_URL = 'https://adora.media/learn/one-link-engine';
const TITLE = 'What is a One-Link Engine? Print Once, Decide Later, Track Everything | Adora';
const DESCRIPTION = 'A one-link engine is a single durable URL that adapts to whatever the campaign needs — a chooser, an OS-aware app store redirect, a feedback form, or a product gallery — with one tracking spine underneath. Learn how it works and when to use it.';
// FAQ content lives in code so it can hydrate two surfaces: the visible
// accordion AND the schema.org FAQPage JSON-LD that AI Overviews + Google's
// rich-result indexer parse to lift answers into search.
const FAQS = [
    {
        question: 'What is a one-link engine?',
        answer: 'A one-link engine is a single, durable smart link whose underlying behavior is configurable. The printed URL or QR code never changes, but the page it serves can be a link chooser, an OS-aware app store redirect, a feedback form, or a product gallery — and you can switch between those modes without reprinting anything.',
    },
    {
        question: 'How is a one-link engine different from a link-in-bio tool?',
        answer: 'A link-in-bio tool (like Linktree, Beacons, or LinkTree-style profiles) always serves the same kind of page: a list of links. A one-link engine swaps the entire page type behind the link — list view this week, product gallery next week, feedback form after that — while keeping the same URL and a single, unified analytics stream.',
    },
    {
        question: 'How is a one-link engine different from a URL shortener?',
        answer: 'A URL shortener (Bitly, TinyURL) maps one short URL to one destination. Editing the destination is possible but you only get click counts. A one-link engine renders a configurable landing page or smart redirect, and every scan is captured as a full scan record with device, browser, location, and per-mode click attribution.',
    },
    {
        question: 'Can I change the mode without reprinting the QR code?',
        answer: 'Yes — that is the entire point. The QR or short URL points to a stable Smart Link. Changing the campaign mode (classic, auto_store, feedback, or showroom) only updates what gets rendered; the public URL stays identical, and the scan history accumulated under it is preserved.',
    },
    {
        question: 'Do I get one analytics dashboard across modes?',
        answer: 'Yes. Every scan — regardless of mode — writes to a single unified scan log, so the dashboard, comparison views, exports, and filters all behave the same whether the link is currently a feedback form or a showroom. Mode switches do not fragment your data.',
    },
    {
        question: 'Who is a one-link engine for?',
        answer: 'Small and medium businesses running offline-to-online campaigns: restaurants with table-tent menus, retailers with packaging QR codes, app developers driving store installs, real-estate agents with property flyers, and any team that needs printed materials whose digital behavior may evolve over weeks or seasons.',
    },
];
const ARTICLE_SCHEMA = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'What is a One-Link Engine?',
    description: DESCRIPTION,
    url: PAGE_URL,
    mainEntityOfPage: PAGE_URL,
    inLanguage: 'en',
    author: { '@type': 'Organization', name: 'Adora Analytics', url: 'https://adora.media' },
    publisher: {
        '@type': 'Organization',
        name: 'Adora Analytics',
        url: 'https://adora.media',
        logo: {
            '@type': 'ImageObject',
            url: 'https://adora.media/icons/icon-512x512.png',
        },
    },
    about: [
        { '@type': 'Thing', name: 'Smart Links' },
        { '@type': 'Thing', name: 'QR code analytics' },
        { '@type': 'Thing', name: 'Offline marketing measurement' },
        { '@type': 'Thing', name: 'App store redirect' },
    ],
};
const FAQ_SCHEMA = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
};
export const Route = createFileRoute('/_layout/learn/one-link-engine/')({
    component: LearnOneLinkEnginePage,
    head: () => ({
        meta: [
            { title: TITLE },
            { name: 'description', content: DESCRIPTION },
            {
                name: 'keywords',
                content: 'one-link engine, smart link, QR code analytics, link-in-bio alternative, URL shortener alternative, offline marketing, app store redirect, feedback link, product gallery link, campaign tracking',
            },
            { property: 'og:type', content: 'article' },
            { property: 'og:url', content: PAGE_URL },
            { property: 'og:title', content: TITLE },
            { property: 'og:description', content: DESCRIPTION },
            { name: 'twitter:title', content: TITLE },
            { name: 'twitter:description', content: DESCRIPTION },
        ],
        links: [{ rel: 'canonical', href: PAGE_URL }],
    }),
});
function LearnOneLinkEnginePage() {
    return (<main className="mx-auto max-w-6xl px-6 md:px-8 py-12">
            {/* Schema.org JSON-LD — feeds Google rich results + AI Overviews. */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ARTICLE_SCHEMA) }}/>
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_SCHEMA) }}/>

            {/* HERO */}
            <article className="space-y-12">
                <header className="text-center max-w-3xl mx-auto space-y-3">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Learn · Smart Links</p>
                    <h1 className="text-3xl md:text-4xl font-bold">What is a one-link engine?</h1>
                    <p className="text-muted-foreground">
                        Print once, decide later, measure everything. A primer on the model behind Adora.
                    </p>
                </header>

                <Separator />

                {/* TL;DR — written for AI Overviews + ChatGPT/Perplexity extraction. */}
                <section aria-labelledby="tldr" className="max-w-3xl mx-auto">
                    <Card className="border-primary/30">
                        <CardHeader>
                            <CardTitle id="tldr" className="text-base uppercase tracking-wider text-primary">
                                The short answer
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="text-base leading-relaxed">
                            A <strong>one-link engine</strong> is a single durable URL whose underlying behavior is
                            configurable. The printed link or QR code stays the same forever, but the page it serves can
                            switch between a link chooser, an OS-aware app store redirect, a feedback form, or a product
                            gallery — all backed by one unified analytics stream. Adora is built as a one-link engine:
                            one Smart Link, four campaign modes, and one unified scan record per tap so your offline
                            campaigns stay measurable even as their digital experience evolves.
                        </CardContent>
                    </Card>
                </section>

                <Separator />

                {/* DEFINITION */}
                <section aria-labelledby="definition" className="max-w-3xl mx-auto space-y-4">
                    <h2 id="definition" className="text-2xl font-semibold">
                        Why "engine" instead of just "link"?
                    </h2>
                    <p className="text-muted-foreground">
                        Most link tools force you to pick a behavior up front. A{' '}
                        <Link to="/learn/smart-links-vs-link-in-bio" className="text-primary hover:underline">
                            link-in-bio page
                        </Link>{' '}
                        is always a list of links. A URL shortener is always a redirect to a single fixed destination. A
                        form tool is always a form. If your campaign needs a different shape next month, you make a new
                        link, reprint your materials, and start your analytics over from zero.
                    </p>
                    <p className="text-muted-foreground">
                        A one-link engine inverts that. The link is the durable surface; the behavior is the
                        configuration. The QR code on the table tent doesn't know — or care — whether today it serves a
                        menu chooser, a Telegram feedback form, or a photo-led product showroom. You change the
                        configuration; the printed material stays valid; the scan history keeps building.
                    </p>
                </section>

                <Separator />

                {/* FOUR MODES */}
                <section aria-labelledby="modes">
                    <div className="text-center max-w-2xl mx-auto space-y-2 mb-8">
                        <h2 id="modes" className="text-2xl font-semibold">
                            How a one-link engine works: four modes, one link
                        </h2>
                        <p className="text-muted-foreground text-sm">
                            Each Adora smart link belongs to a campaign in one of four modes. Switch modes anytime — the
                            public URL never changes.
                        </p>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                        <ModeCard icon={<SquareStack className="h-5 w-5"/>} title="Classic mode — a link chooser" text="A clean landing page with a list of tracked options. Best when visitors should pick where to go themselves: menu items, social channels, sub-pages, product variants."/>
                        <ModeCard icon={<Smartphone className="h-5 w-5"/>} title="Auto Store mode — OS-aware app redirect" text="Detects the visitor's operating system and redirects straight to the Apple App Store or Google Play. One link covers both stores, with a tracked desktop fallback. No more printing two QR codes."/>
                        <ModeCard icon={<MessageSquare className="h-5 w-5"/>} title="Feedback mode — private intake form" text="A short form whose submissions drop straight into your Telegram chat. The link becomes a private intake channel: no spreadsheet middleman, no email digest, no third-party form builder."/>
                        <ModeCard icon={<ImageIcon className="h-5 w-5"/>} title="Showroom mode — photo-led product gallery" text="A grid of product cards, each with its own image, price label, and tracked tap. Built for restaurant menus, real-estate lookbooks, storefront catalogs, and event programs."/>
                    </div>
                </section>

                <Separator />

                {/* COMPARISON TABLE — structured for AI extraction. */}
                <section aria-labelledby="comparison">
                    <h2 id="comparison" className="text-2xl font-semibold mb-6 text-center">
                        One-link engine vs. link-in-bio vs. URL shortener
                    </h2>

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                            <thead className="bg-muted/40 text-left">
                                <tr>
                                    <th className="p-3 font-semibold">Capability</th>
                                    <th className="p-3 font-semibold">URL shortener</th>
                                    <th className="p-3 font-semibold">Link-in-bio tool</th>
                                    <th className="p-3 font-semibold text-primary">One-link engine</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border text-muted-foreground">
                                <CompareRow label="Same URL, different page types" shortener="No" bio="No (always a list)" engine="Yes (4 modes)"/>
                                <CompareRow label="OS-aware app store redirect" shortener="No" bio="No" engine="Yes (Auto Store mode)"/>
                                <CompareRow label="Private feedback intake" shortener="No" bio="External widget" engine="Yes (Feedback mode + Telegram)"/>
                                <CompareRow label="Photo-led product gallery" shortener="No" bio="Limited (avatar list)" engine="Yes (Showroom mode)"/>
                                <CompareRow label="Unified per-scan analytics" shortener="Click counts only" bio="Per-link clicks" engine="Full scan record (device, browser, dedupe, attribution)"/>
                                <CompareRow label="Mode switch without reprinting" shortener="N/A" bio="N/A" engine="Yes"/>
                            </tbody>
                        </table>
                    </div>
                </section>

                <Separator />

                {/* WHY IT MATTERS */}
                <section aria-labelledby="why">
                    <div className="text-center max-w-2xl mx-auto space-y-2 mb-8">
                        <h2 id="why" className="text-2xl font-semibold">
                            Why a one-link engine matters for offline campaigns
                        </h2>
                        <p className="text-muted-foreground text-sm">
                            Printed materials live for months. Digital strategies don't. The mismatch is where most
                            offline campaigns leak measurement.
                        </p>
                    </div>

                    <div className="grid gap-4 md:grid-cols-3">
                        <ValueCard icon={<QrCode className="h-5 w-5"/>} title="Durable physical surface" text="The printed QR code doesn't care which mode is active. Picked the wrong mode at launch? Switch in seconds. Nothing on paper needs reprinting, no posters go to waste."/>
                        <ValueCard icon={<RefreshCw className="h-5 w-5"/>} title="Swappable digital surface" text="Run a feedback drive for two weeks, then flip the same link to a seasonal showroom. The QR on the table keeps working, and the new mode inherits the existing scan history."/>
                        <ValueCard icon={<BarChart3 className="h-5 w-5"/>} title="Unified analytics spine" text="Every scan writes to one unified record, regardless of mode. Dashboards, comparison views, and exports stay coherent — you never juggle three vendors' analytics."/>
                    </div>
                </section>

                <Separator />

                {/* WHEN TO USE WHICH MODE */}
                <section aria-labelledby="when" className="max-w-4xl mx-auto space-y-4">
                    <h2 id="when" className="text-2xl font-semibold text-center">
                        When to use which mode
                    </h2>
                    <p className="text-muted-foreground text-sm text-center max-w-2xl mx-auto">
                        A one-link engine doesn't replace the need for product judgment. Use this as a quick decision
                        guide.
                    </p>

                    <div className="grid gap-4 md:grid-cols-2">
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base">Use Classic when…</CardTitle>
                            </CardHeader>
                            <CardContent className="text-sm text-muted-foreground space-y-1">
                                <p>
                                    Visitors need to choose between several destinations (menu sections, social
                                    profiles, product categories) and you want per-option click tracking.
                                </p>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base">Use Auto Store when…</CardTitle>
                            </CardHeader>
                            <CardContent className="text-sm text-muted-foreground space-y-1">
                                <p>
                                    You're driving app installs. One QR on a flyer, packaging, or billboard sends iOS
                                    visitors to App Store and Android visitors to Google Play, automatically.
                                </p>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base">Use Feedback when…</CardTitle>
                            </CardHeader>
                            <CardContent className="text-sm text-muted-foreground space-y-1">
                                <p>
                                    You need short structured input from customers (a complaint, a rating, a service
                                    request) delivered directly to a Telegram chat without spreadsheet or email
                                    middleware.
                                </p>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base">Use Showroom when…</CardTitle>
                            </CardHeader>
                            <CardContent className="text-sm text-muted-foreground space-y-1">
                                <p>
                                    The content is visual: a restaurant menu with food photography, a real-estate
                                    lookbook, a retail catalog, a seasonal lineup. Each card is a tracked tap.
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                </section>

                <Separator />

                {/* FAQ — visible accordion + JSON-LD schema above. */}
                <section aria-labelledby="faq" className="max-w-3xl mx-auto">
                    <h2 id="faq" className="text-2xl font-semibold mb-6">
                        Frequently asked questions
                    </h2>

                    <Accordion type="single" collapsible className="w-full">
                        {FAQS.map((faq, i) => (<AccordionItem key={faq.question} value={`item-${i}`}>
                                <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                                <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
                            </AccordionItem>))}
                    </Accordion>
                </section>

                <Separator />

                {/* RELATED READING — internal-link cluster for SEO. */}
                <section aria-labelledby="related" className="max-w-4xl mx-auto">
                    <h2 id="related" className="text-2xl font-semibold mb-6">
                        Related reading
                    </h2>
                    <div className="grid gap-3 md:grid-cols-2 text-sm">
                        <RelatedLink to="/learn/smart-links" title="What are Smart Links?"/>
                        <RelatedLink to="/learn/smart-links-vs-link-in-bio" title="Smart Links vs. link-in-bio"/>
                        <RelatedLink to="/learn/qr-scan-tracking" title="How QR scan tracking works"/>
                        <RelatedLink to="/learn/offline-marketing-analytics" title="A guide to offline marketing analytics"/>
                        <RelatedLink to="/features" title="Adora features"/>
                        <RelatedLink to="/compare" title="Compare Adora with other tools"/>
                    </div>
                </section>

                <Separator />

                {/* CTA */}
                <Card>
                    <CardContent className="p-6 md:p-8 flex flex-col md:flex-row gap-6 items-start md:items-center justify-between">
                        <div>
                            <h2 className="text-xl font-semibold">Print once. Decide later.</h2>
                            <p className="text-muted-foreground text-sm mt-1 max-w-xl">
                                Spin up a smart link in any mode, switch the mode whenever the campaign changes, and
                                keep every scan in one analytics dashboard.
                            </p>
                        </div>

                        <div className="flex gap-2">
                            <Button asChild>
                                <Link to="/sign-up" search={{ redirect: '/console' }}>
                                    Start free
                                </Link>
                            </Button>
                            <Button asChild variant="outline">
                                <Link to="/pricing">See pricing</Link>
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </article>
        </main>);
}
function ModeCard({ icon, title, text }) {
    return (<Card className="h-full">
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                        {icon}
                    </span>
                    {title}
                </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">{text}</CardContent>
        </Card>);
}
function ValueCard({ icon, title, text }) {
    return (<Card className="h-full">
            <CardContent className="p-5 space-y-2">
                <div className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                    {icon}
                </div>
                <div className="font-medium">{title}</div>
                <div className="text-sm text-muted-foreground">{text}</div>
            </CardContent>
        </Card>);
}
function CompareRow({ label, shortener, bio, engine, }) {
    return (<tr>
            <td className="p-3 font-medium text-foreground">{label}</td>
            <td className="p-3">{shortener}</td>
            <td className="p-3">{bio}</td>
            <td className="p-3 text-primary font-medium">{engine}</td>
        </tr>);
}
function RelatedLink({ to, title }) {
    return (<Link to={to} className="group flex items-center justify-between rounded-md border border-border p-3 hover:border-primary hover:bg-muted/40 transition">
            <span className="font-medium">{title}</span>
            <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary"/>
        </Link>);
}
